import { NextSeo } from 'next-seo';
import { useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { gql, useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { useIsomorphicEffect } from 'rooks';
import cx from 'classnames';

import { useCampaignContribute } from 'context/CampaignContribute';
import useScreen from 'hooks/useScreen';
import CampaignContributeLayout from 'components/layouts/CampaignContributeLayout';
import Button from 'components/common/Button';
import Card from 'components/common/Card';
import CampaignContributeTitle from 'components/campaign-contribute/CampaignContributeTitle';

const GET_CAMPAIGN_SELECT_PAGE = gql`
  query GetCampaignSelectPage($id: String!) {
    findCampaigns(id: $id) {
      id
      activeFeatures {
        teams
      }
      supportedFeatures {
        join
      }
      fundraiserCount
    }
  }
`;

const CampaignContributeSelectPage = () => {
  const router = useRouter();
  const screen = useScreen();
  const { campaignId, updateContribution } = useCampaignContribute();

  const [selected, setSelected] = useState(null);

  const { data } = useQuery(GET_CAMPAIGN_SELECT_PAGE, { variables: { id: campaignId } });
  const campaign = useMemo(() => data?.findCampaigns[0], [data]);
  const teamsEnabled = campaign?.activeFeatures.teams;

  const recipientUrl = `/c/${campaignId}/contribute/recipient`;
  const amountUrl = `/c/${campaignId}/contribute/amount`;

  useIsomorphicEffect(() => {
    if (!campaign) return;

    if (!campaign.supportedFeatures.join || campaign.fundraiserCount === 0) {
      updateContribution({ type: 'donation' });
      router.replace(amountUrl);
    }
  }, [campaign, router, amountUrl]);

  if (!campaign) return null;
  return (
    <>
      <NextSeo title="Select the campaign or team or fundraiser to support" />
      <div className="mt-10 mx-auto lg:max-w-lg">
        <Card at="sm" depth="none" className="lg:p-10" border={screen.sm}>
          <CampaignContributeTitle
            className="mb-6 text-center"
            dataTestId="contribute-select-title"
          >
            How would you like to donate?
          </CampaignContributeTitle>
          <div className="flex flex-col gap-y-2.5">
            <button
              type="button"
              onClick={() => {
                setSelected('general');
                // Reset team & fundraiser IDs in case user has come
                // back to this page after initially selecting one
                updateContribution({ teamId: null, fundraiserId: null });
              }}
              className={cx(
                'flex items-center justify-between border border-gray-300 bg-white px-6 py-5 rounded-xl w-full text-left hover:border-gray-800 hover:shadow-lg',
                {
                  'border-2 border-theme-primary': selected === 'general',
                }
              )}
            >
              <p className="font-medium">Make a general donation</p>
              <div
                className={cx(
                  'shrink-0 rounded-full border border-gray-400 h-5 w-5 flex items-center justify-center',
                  { 'bg-theme-primary': selected === 'general' }
                )}
              >
                {selected === 'general' && (
                  <FontAwesomeIcon icon={faCheckCircle} size="xl" className="text-white" />
                )}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setSelected('fundraiser')}
              className={cx(
                'flex items-center justify-between border border-gray-300 bg-white px-6 py-5 rounded-xl w-full text-left hover:border-gray-800 hover:shadow-lg',
                {
                  'border-2 border-theme-primary': selected === 'fundraiser',
                }
              )}
            >
              <p className="font-medium">
                Give credit to a fundraiser {teamsEnabled ? 'or team' : ''}
              </p>
              <div
                className={cx(
                  'shrink-0 rounded-full border border-gray-400 h-5 w-5 flex items-center justify-center',
                  { 'bg-theme-primary': selected === 'fundraiser' }
                )}
              >
                {selected === 'fundraiser' && (
                  <FontAwesomeIcon icon={faCheckCircle} size="xl" className="text-white" />
                )}
              </div>
            </button>
          </div>
          <div className="flex item-center justify-center mt-8">
            <Button
              href={selected === 'fundraiser' ? recipientUrl : amountUrl}
              color="primary"
              className="font-medium w-full"
              radius="full"
              disabled={selected === null}
            >
              Continue
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
};

CampaignContributeSelectPage.getLayout = (page, router) => (
  <CampaignContributeLayout pageTitleDetail="Select donation type" id={router.query.id}>
    {page}
  </CampaignContributeLayout>
);

export default CampaignContributeSelectPage;
